import { useState, useCallback, useLayoutEffect } from 'react';
import { Box, Button, Flex, Image, Portal, Text } from '@chakra-ui/react';
import { Avatar } from '@halo/ui-chat-react';
import { Dialog, toaster } from '@halo/ui-react';
import { walletTools } from '@halo/wallet-app';
import { MsgPayloadType, WalletTransferStatus, type SessionStore } from '@halo/chat-sdk-react';
import { useWalletStore } from '@/wallet';
import { useTransferStore } from '@/store/transfer';
import { usePortalContainer } from '@/scripts/hooks';
import { DetailList } from '../DetailList';
import { formatAddress } from '../helpers';

interface TransferConfirmDialogProps {
  isOpen?: boolean;
  sessionStore?: SessionStore;
  setIsOpen?: (isOpen: boolean) => void;
  onConfirm: () => void;
  transferConfirmDialogData: any;
}
export function TransferConfirmDialog({
  isOpen,
  setIsOpen,
  sessionStore,
  transferConfirmDialogData,
  onConfirm,
}: TransferConfirmDialogProps) {
  const { t } = useTranslation();
  const { user, sendTo, message, amount, to, coin, gasFeeResult } =
    transferConfirmDialogData || {};
  const { totalGasFee } = gasFeeResult || {};
  const { client, updateMessages } = sessionStore || {};
  const { coinAmountDisplay, currencyAmountDisplay, transaction, readonly } = useWalletStore();
  const { getWalletTransferByTransferId } = useTransferStore();

  const [open, setOpen] = useState(isOpen);
  const [loading, setLoading] = useState(false);

  const { portalContainerRef } = usePortalContainer();

  const handleConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const { chat_id, account, coin, amount, from, to, message, sendTo, gasFeeResult } =
        transferConfirmDialogData;

      const payloadInfo = {
        chainType: coin.chainType,
        chainId: coin.chainId,
        symbol: coin.symbol,
        amount,
        desc: message,
        chainIcon: coin.chainLogo,
        tokenIcon: coin.logo,
        from,
        to,
        sendTo: sendTo.user_id,
        txFee: gasFeeResult?.totalGasFee?.gasFee,
      };

      const payload = JSON.stringify(payloadInfo);

      const res = await transaction(
        account.id,
        coin.netId,
        coin,
        {
          to,
          amount,
          ...gasFeeResult.gasLevelInfo?.info,
          isEIP1559: gasFeeResult.evmPaymentMethod === gasFeeResult.EIP1559,
        },
        {
          businessType: 'chatTransaction',
          businessParams: {
            accountId: account.id,
            chainType: coin.chainType,
            symbol: coin.symbol,
            mainSymbol: gasFeeResult.mainCoin?.symbol,
            amount,
            desc: message,
            chainIcon: coin.chainLogo,
            tokenIcon: coin.logo,
            from,
            to,
            sendTo: sendTo.user_id,
            gasInfo: { ...gasFeeResult.gasInfo, ...gasFeeResult.gasLevelInfo?.info },
          },
        }
      ).catch(async (err) => {
        if (!readonly) {
          toaster.error({
            title: err?.message || 'Failed to send transaction',
          });
        }
        throw err;
      });

      walletTools.hideWalletApp();

      const cliMessageId = await client?.Api.messages.saveLocalMessage(
        chat_id,
        payload,
        MsgPayloadType.TRANSFER
      );

      if (res?.hash) {
        client?.Api.wallet.addWalletTransfer({
          chat_id,
          cli_msg_id: cliMessageId!,
          chain_id: payloadInfo.chainId.toString(),
          chain_type: payloadInfo.chainType,
          transfer_id: res.hash,
          status: WalletTransferStatus.Pending,
        });
        await client?.Api.messages.editLocalMessage(
          chat_id,
          cliMessageId!,
          JSON.stringify({
            ...payloadInfo,
            txId: res.hash,
          })
        );
        updateMessages?.([
          {
            cli_msg_id: cliMessageId!,
            payload: {
              txId: res.hash,
            },
          },
        ]);
        getWalletTransferByTransferId({ transfer_id: res.hash });
        setOpen(false);
        setIsOpen?.(false);
      }
      onConfirm?.();
    } catch (error) {
      console.log(error, 'error');
    } finally {
      setLoading(false);
    }
  }, [
    transferConfirmDialogData,
    client?.Api,
    readonly,
    transaction,
    onConfirm,
    updateMessages,
    getWalletTransferByTransferId,
    setIsOpen,
  ]);

  useLayoutEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        setIsOpen?.(details.open);
      }}
      lazyMount
      unmountOnExit
      restoreFocus={false}
      trapFocus={false}
      closeOnInteractOutside={false}
    >
      <Portal container={portalContainerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner closeOnInteractOutside={false}>
          <Dialog.Content
            borderRadius="2xl"
            w="428px"
            maxW="100%"
            p="0"
            overflow="hidden"
            bg="grey.10"
          >
            <Dialog.Header
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
              pt="5"
              px="5"
              pb="0"
            >
              <Dialog.Title fontSize="md" fontWeight="semibold">
                {t('chat.confirmTransferText')}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body p="5">
              <Box className="ConfirmTransfer">
                <Box className="DetailCard" bg="white" borderRadius="lg" px="1" py="2">
                  <DetailList
                    title={
                      <Flex gap="1" alignItems="center">
                        <Box>{t('chat.fromText')}</Box>
                        <Flex
                          bg="grey.50"
                          borderRadius="xl"
                          alignItems="center"
                          px="2"
                          py="1.5"
                          overflow="hidden"
                        >
                          <Box fontSize="sm" fontWeight="semibold" color="grey.97" truncate>
                            {user?.name}
                          </Box>
                        </Flex>
                      </Flex>
                    }
                    content=""
                  />
                  <DetailList
                    title={
                      <Flex gap="1" alignItems="center">
                        <Box flexShrink="0">{t('chat.sendToText')}</Box>
                        <Flex
                          bg="grey.50"
                          borderRadius="xl"
                          alignItems="center"
                          px="2"
                          py="1.5"
                          overflow="hidden"
                        >
                          <Avatar w="5" h="5" name={sendTo?.name} src={sendTo?.avatar} mr="1" />
                          <Box fontSize="sm" fontWeight="semibold" color="grey.97" truncate>
                            {sendTo?.name}
                          </Box>
                          <Box fontWeight="medium" color="grey.92">
                            @{sendTo?.username}
                          </Box>
                        </Flex>
                      </Flex>
                    }
                    content={<Text color="grey.92">({formatAddress(to)})</Text>}
                  />
                  <DetailList
                    title={t('chat.symbolText')}
                    content={
                      <Flex alignItems="center" gap="0.5">
                        <Image borderRadius="full" w="4" h="4" src={coin?.logo} />
                        <Text fontSize="sm">{coin?.symbol}</Text>
                        {coin?.contract && (
                          <Text color="grey.92">({formatAddress(coin.contract)})</Text>
                        )}
                      </Flex>
                    }
                  />
                  <DetailList title={t('chat.amountText')} content={amount || ''} />
                  <DetailList
                    title={t('chat.gasFeeText')}
                    content={
                      totalGasFee?.gasFee
                        ? `${coinAmountDisplay(totalGasFee?.gasFee, coin.symbol)}  ${currencyAmountDisplay(totalGasFee?.currencyGasFee, '≈')}`
                        : ''
                    }
                  />
                  <DetailList title={t('chat.messageText')} content={message || ''} />
                </Box>
              </Box>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="plain" disabled={loading}>
                  {t('buttons.cancel')}
                </Button>
              </Dialog.ActionTrigger>
              <Button onClick={handleConfirm} loading={loading}>
                {t('buttons.confirm')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
